import {
  Button,
  Css,
  PageHeaderLayout,
  TabContent,
  TabWithContent,
  useComputed,
  useSessionStorage,
  useSnackbar,
} from "@homebound/beam";
import { useFormStates } from "@homebound/form-state";
import { ReactNode, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { NavigationBlocker } from "src/components/navigation/NavigationBlocker";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { DesignPackageLocation, FeatureFlagType } from "src/generated/graphqlTypes";
import { ConfirmLeaveChangesModal } from "src/routes/admin/finishSchedule/ConfirmLeaveChangesModal";
import {
  FinishScheduleCostCodeFragment,
  useFinishScheduleCostCodesQuery,
  useSaveFinishScheduleCostCodesMutation,
  useSaveFinishScheduleItemsMutation,
} from "src/routes/admin/finishSchedule/FinishScheduleAdmin.generated";
import {
  finishScheduleCostCodeConfig,
  FinishScheduleCostCodeForm,
  FinishScheduleCostCodeTable,
  FinishScheduleItem,
  finishScheduleItemConfig,
  FinishScheduleItemForm,
} from "src/routes/admin/finishSchedule/FinishScheduleCostCodeTable";
import { adminRoute } from "src/routing/domains/adminPaths";
import { queryResult } from "src/utils";

export function FinishScheduleAdminPage() {
  const isEnabled = useFeatureFlag(FeatureFlagType.FinishSchedulesV2);

  const query = useFinishScheduleCostCodesQuery({ variables: { filter: { version: [2] } }, skip: !isEnabled });

  if (!isEnabled) return <Navigate to={adminRoute.url()} replace />;

  return queryResult(query, { data: ({ costCodes }) => <FinishScheduleAdminPageView costCodes={costCodes} /> });
}

type FinishScheduleAdminPageViewProps = { costCodes: FinishScheduleCostCodeFragment[] };

function FinishScheduleAdminPageView(props: FinishScheduleAdminPageViewProps) {
  const { costCodes } = props;
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useSessionStorage<DesignPackageLocation>(
    "finishScheduleTabV2",
    DesignPackageLocation.Interior,
  );

  // One mini-form per cost code / item so each Order input tracks its own dirty state. The hook
  // caches an ObjectState per row, so we re-derive the full set from `costCodes` on demand (below)
  // rather than tracking them by hand — mirrors VoidPOsStep / MapItemsStep.
  const { getFormState } = useFormStates<FinishScheduleCostCodeForm, FinishScheduleCostCodeFragment>({
    config: finishScheduleCostCodeConfig,
    getId: (cc) => cc.id,
    map: (cc) => ({
      id: cc.id,
      interiorFinishScheduleOrder: cc.interiorFinishScheduleOrder,
      exteriorFinishScheduleOrder: cc.exteriorFinishScheduleOrder,
    }),
  });
  const { getFormState: getItemFormState } = useFormStates<FinishScheduleItemForm, FinishScheduleItem>({
    config: finishScheduleItemConfig,
    getId: (item) => item.id,
    map: (item) => ({ id: item.id, finishScheduleOrder: item.finishScheduleOrder }),
  });

  // Every row's ObjectState across both tabs (getFormState returns the cached instance per row).
  const costCodeForms = useComputed(() => costCodes.map((cc) => getFormState(cc)), [costCodes, getFormState]);
  const itemForms = useComputed(
    () => costCodes.flatMap((cc) => cc.items).map((item) => getItemFormState(item)),
    [costCodes, getItemFormState],
  );
  const isDirty = useComputed(
    () => [...costCodeForms, ...itemForms].some((os) => os.dirty),
    [costCodeForms, itemForms],
  );

  const { triggerNotice } = useSnackbar();
  const [saveCostCodes] = useSaveFinishScheduleCostCodesMutation();
  const [saveItems] = useSaveFinishScheduleItemsMutation();
  const onSave = useCallback(async () => {
    // Cost-code order saves via saveCostCodes; item order via saveItems. Only `id` + order are on
    // each form, so `changedValue` never touches other fields.
    const costCodesToSave = costCodeForms.filter((os) => os.dirty && os.canSave());
    const itemsToSave = itemForms.filter((os) => os.dirty && os.canSave());
    if (costCodesToSave.isEmpty && itemsToSave.isEmpty) return;
    // Two mutations because the page edits two entity types; batch each into a single call.
    await Promise.all([
      costCodesToSave.nonEmpty
        ? saveCostCodes({ variables: { input: { costCodes: costCodesToSave.map((os) => os.changedValue) } } })
        : undefined,
      itemsToSave.nonEmpty
        ? saveItems({ variables: { input: { items: itemsToSave.map((os) => os.changedValue) } } })
        : undefined,
    ]);
    triggerNotice({ message: "Finish schedule order saved", icon: "success" });
  }, [costCodeForms, itemForms, saveCostCodes, saveItems, triggerNotice]);

  function renderSection(heading: string, designPackageLocation: DesignPackageLocation): ReactNode {
    return (
      <div css={Css.mxa.wPx(720).maxw100.$}>
        <h1 css={Css.lg.mt6.$}>{heading}</h1>
        <div css={Css.sm.mt2.mb3.$}>
          Configure the structure and columns for {heading} exports by reordering each section. Click into each cost
          code group to further edit.
        </div>
        <FinishScheduleCostCodeTable
          designPackageLocation={designPackageLocation}
          costCodes={costCodes}
          getFormState={getFormState}
          getItemFormState={getItemFormState}
        />
      </div>
    );
  }

  const tabs: TabWithContent<DesignPackageLocation>[] = [
    {
      name: "Interior",
      value: DesignPackageLocation.Interior,
      render: () => renderSection("Interior Finish Schedules", DesignPackageLocation.Interior),
    },
    {
      name: "Exterior",
      value: DesignPackageLocation.Exterior,
      render: () => renderSection("Exterior Finish Schedules", DesignPackageLocation.Exterior),
    },
  ];

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Finish Schedules",
        rightSlot: (
          <div css={Css.df.gapPx(10).$}>
            <Button label="Cancel" variant="tertiary" onClick={() => navigate(adminRoute.url())} />
            {/* TODO(APP-1612) */}
            <Button label="Preview" variant="secondary" disabled onClick={() => {}} />
            <Button label="Save" onClick={onSave} />
          </div>
        ),
        tabs: { selected: selectedTab, onChange: setSelectedTab, tabs },
      }}
    >
      <TabContent tabs={tabs} selected={selectedTab} contentXss={Css.pt2.$} />
      {/* React Router confirmation dialog; mirrors src/components/formFields/FormActions.tsx. */}
      <NavigationBlocker when={() => isDirty}>
        {({ onConfirm, onCancel }) => (
          <ConfirmLeaveChangesModal
            onConfirm={onConfirm}
            onCancel={onCancel}
            revertAll={() => [...costCodeForms, ...itemForms].forEach((os) => os.revertChanges())}
          />
        )}
      </NavigationBlocker>
    </PageHeaderLayout>
  );
}
